import { Component } from "react";
import { AlertTriangle, RefreshCw } from "lucide-react";

/**
 * ErrorBoundary — isole les crashs React à un sous-arbre.
 * Utilisation :
 *   <ErrorBoundary name="KpiCADaily"><KpiCADaily /></ErrorBoundary>
 * Si un composant plante (ex : après un import), seul ce bloc affiche
 * l'erreur, le reste de l'application (menu, sidebar, autres pages) reste
 * fonctionnel.
 */
export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, resetKey: 0 };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // Log dans la console pour le debug, non envoyé au backend pour l'instant.
    // eslint-disable-next-line no-console
    console.error(`[ErrorBoundary${this.props.name ? `:${this.props.name}` : ""}]`, error, info?.componentStack);
  }

  reset = () => {
    this.setState((s) => ({ hasError: false, error: null, resetKey: s.resetKey + 1 }));
  };

  render() {
    if (!this.state.hasError) {
      // resetKey change → force le remount du sous-arbre après reset
      return <div key={this.state.resetKey}>{this.props.children}</div>;
    }
    const compact = this.props.compact;
    return (
      <div
        data-testid={`error-boundary-${this.props.name || "generic"}`}
        className={`${compact ? "p-4" : "p-6 md:p-10"} m-4 rounded-2xl border border-rose-200 bg-rose-50/70 text-rose-900`}
      >
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-rose-500 text-white grid place-items-center shrink-0 shadow-sm">
            <AlertTriangle className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-display font-bold text-lg">
              Un composant a rencontré une erreur
            </div>
            <div className="text-sm text-rose-800/80 mt-1">
              {this.props.name ? (
                <>La section <span className="font-semibold">{this.props.name}</span> n'a pas pu s'afficher.</>
              ) : (
                <>Cette section n'a pas pu s'afficher.</>
              )}{" "}
              Le reste d'Orbit reste utilisable — vous pouvez rafraîchir uniquement cette section.
            </div>
            {this.state.error?.message && (
              <div className="mt-3 text-xs font-mono bg-white/60 border border-rose-200 rounded-lg p-2 overflow-x-auto max-h-32">
                {String(this.state.error.message)}
              </div>
            )}
            <div className="mt-4 flex flex-wrap gap-2">
              <button
                data-testid="error-boundary-reset"
                onClick={this.reset}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-rose-600 text-white text-sm font-semibold hover:bg-rose-700 transition-colors"
              >
                <RefreshCw className="w-4 h-4" /> Recharger la section
              </button>
              <button
                data-testid="error-boundary-reload"
                onClick={() => window.location.reload()}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-white border border-rose-300 text-rose-700 text-sm font-semibold hover:bg-rose-50 transition-colors"
              >
                Recharger la page
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }
}
